import { PlotReport } from '@/api/rest/resources/types/dashboard';
import {
  IndicatorFactType,
  PlotReportFactElement,
  PlotReportFactLabel,
  PlotReportFactLabelFilter,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { OrganizationReportingFacts } from '@/api/rest/resources/types/organization';
import { ProjectReport } from '@/api/rest/resources/types/project';

export const hasLabel = (filterLabel: PlotReportFactLabelFilter, labels: PlotReportFactLabel[]) => {
  return Object.entries(filterLabel).every(([key, value]) =>
    labels.some((label) => label.name === key && label.value === value),
  );
};

export const filterValidLabels = (labels: PlotReportFactLabelFilter) => {
  const allowedLabelFilters = Object.keys(PlotReportFactLabelNameEnum);
  return Object.fromEntries(
    Object.keys(labels)
      .filter((filter) => allowedLabelFilters.includes(filter))
      .map((filter) => [filter, labels[filter as keyof typeof PlotReportFactLabelNameEnum]]),
  );
};

// PlotReportFactLabels that are used to filter/update scenarios
enum ScenarioPlotReportFactLabelNameEnum {
  carbon_input = PlotReportFactLabelNameEnum.carbon_input,
  tillage = PlotReportFactLabelNameEnum.tillage,
  land_use = PlotReportFactLabelNameEnum.land_use,
}
export const filterValidLabelsForScenario = (labels: PlotReportFactLabelFilter) => {
  const allowedLabelFilters = Object.keys(ScenarioPlotReportFactLabelNameEnum);
  return Object.fromEntries(
    Object.keys(labels)
      .filter((filter) => allowedLabelFilters.includes(filter))
      .map((filter) => [filter, labels[filter as keyof typeof ScenarioPlotReportFactLabelNameEnum]]),
  );
};

export const getFact = <T = unknown>(
  plotReport: ProjectReport | PlotReport | OrganizationReportingFacts,
  factType: IndicatorFactType,
  factLabelFilters?: PlotReportFactLabelFilter,
  isFactHidden?: boolean,
) => {
  // TODO - MVP-CW remove completely once R1FactType is removed
  // if (!Object.keys(R1FactType).includes(factType)) {
  //   throw Error(`Unrecognised type received: ${factType}`);
  // }

  if (isFactHidden) return undefined;

  return getFactFromFactArray<T>(plotReport.facts, factType, factLabelFilters);
};

export const getFacts = <T = unknown>(
  plotReport: ProjectReport | PlotReport | OrganizationReportingFacts,
  factType: IndicatorFactType[],
  factLabelFilters?: PlotReportFactLabelFilter,
) => {
  return plotReport.facts.filter(
    (f) => factType.includes(f.name) && (factLabelFilters && f.labels ? hasLabel(factLabelFilters, f.labels) : true),
  ) as PlotReportFactElement<T | null>[] | undefined;
};

export const getFactFromFactArray = <T = unknown>(
  facts: PlotReportFactElement<unknown>[] | undefined,
  factType: IndicatorFactType,
  labels?: PlotReportFactLabelFilter,
) => {
  if (!facts) return undefined;

  return facts.find((f) => f.name === factType && (labels && f.labels ? hasLabel(labels, f.labels) : true)) as
    | PlotReportFactElement<T | null>
    | undefined;
};

export const getFactsFromFactArray = <T = unknown>(
  facts: PlotReportFactElement<unknown>[] | undefined,
  factType: IndicatorFactType,
  labels?: PlotReportFactLabelFilter,
) => {
  if (!facts) return undefined;

  return facts.filter((f) => f.name === factType && (labels && f.labels ? hasLabel(labels, f.labels) : true)) as
    | PlotReportFactElement<T | null>[]
    | undefined;
};
